import { useEffect, useRef } from 'react'
import { IStage } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'

/**
 * Onboarding dev tools: a hidden bar that fills or skips the current step so
 * the flow can be walked through without typing real data.
 *
 * Enabled when the app runs on the Vite dev server, or when the page is
 * opened once with `?dev=1` (remembered in localStorage until `?dev=0`).
 * Nothing here touches the backend; "fill" writes dummy values into the
 * current step's inputs and "skip" flips the store flags the step would set.
 *
 * FREE FLOW (on by default while the tools are enabled, toggle in the bar):
 * the wizard works on a detached sandbox record (`FREE_FLOW_ORG_ID`) that is
 * never saved, every step's Continue button is enabled with no input, no
 * step calls the backend to move on, and the stage mapping screen shows demo
 * stages when Follow Up Boss returns none. Turning it off reloads the real
 * record.
 */
export const DEV_TOOLS_EVENT = 'ff-onboarding:dev-tools'
const STORAGE_KEY = 'ff-onboarding:devtools'
const FREE_FLOW_KEY = 'ff-onboarding:freeflow'

/** Organization id of the detached sandbox record used in free flow. */
export const FREE_FLOW_ORG_ID = 'free-flow-sandbox'

export type TDevToolsMode = 'fill' | 'skip'

export interface IDevToolsDetail {
   mode: TDevToolsMode
   /** Set by a step that handled the request itself. */
   handled: boolean
}

export const isDevToolsEnabled = (): boolean => {
   if (typeof window === 'undefined') return false
   try {
      const flag = new URLSearchParams(window.location.search).get('dev')
      if (flag === '1') window.localStorage.setItem(STORAGE_KEY, '1')
      if (flag === '0') window.localStorage.removeItem(STORAGE_KEY)
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return true
   } catch {
      // storage unavailable; fall through
   }
   return Boolean(import.meta.env.DEV)
}

/** Free flow is on whenever the tools are enabled, unless switched off. */
export const isFreeFlow = (): boolean => {
   if (!isDevToolsEnabled()) return false
   try {
      return window.localStorage.getItem(FREE_FLOW_KEY) !== '0'
   } catch {
      return true
   }
}

export const setFreeFlow = (on: boolean) => {
   try {
      window.localStorage.setItem(FREE_FLOW_KEY, on ? '1' : '0')
   } catch {
      // ignore
   }
}

/** Dispatch a request; returns true when a step claimed it. */
export const requestDevTools = (mode: TDevToolsMode): boolean => {
   const detail: IDevToolsDetail = { mode, handled: false }
   window.dispatchEvent(new CustomEvent(DEV_TOOLS_EVENT, { detail }))
   return detail.handled
}

/**
 * Register fill and skip handlers for the step that is on screen. `skip`
 * returns true when it fully handled the request; otherwise the dev bar
 * falls back to a store-only skip.
 */
export const useDevTools = ({
   fill,
   skip,
}: {
   fill?: () => void
   skip?: () => boolean | void
}) => {
   const ref = useRef({ fill, skip })
   ref.current = { fill, skip }
   useEffect(() => {
      if (!isDevToolsEnabled()) return
      const handler = (event: Event) => {
         const detail = (event as CustomEvent<IDevToolsDetail>).detail
         if (!detail) return
         if (detail.mode === 'fill') {
            ref.current.fill?.()
            detail.handled = Boolean(ref.current.fill)
         } else if (ref.current.skip) {
            detail.handled = ref.current.skip() !== false
         }
      }
      window.addEventListener(DEV_TOOLS_EVENT, handler)
      return () => window.removeEventListener(DEV_TOOLS_EVENT, handler)
   }, [])
}

/** Dummy values used by "fill this step". */
export const DEV_DUMMY = {
   fubApiKey:
      (import.meta.env.VITE_APP_DEV_FUB_API_KEY as string | undefined) ||
      'dev-fub-api-key',
   seats: 3,
   maxSpend: 200,
   card: { nameOnCard: 'Test Agent', city: 'Denver', billingZIPCode: '80202' },
   areaCodes: ['303', '720', '719'],
}

/** Demo Follow Up Boss stages, tags and suggestions for free flow. */
export const DEMO_STAGES: IStage[] = [
   'New Lead',
   'Attempted Contact',
   'Nurture',
   'Hot Prospect',
   'Appointment Set',
   'Appointment Missed',
   'Active Client',
   'Under Contract',
   'Closed',
   'Trash',
   'Sphere',
].map((name, i) => ({ id: String(i + 1), name, description: '', type: 'stage' }))

export const DEMO_TAGS: IStage[] = [
   'zillow',
   'buyer',
   'seller',
   'investor',
   'do-not-call',
   'sold',
   'has-agent',
   'relocation',
].map((name) => ({ id: name, name, description: '', type: 'tag' }))

const demoStage = (name: string): IStage =>
   DEMO_STAGES.find((s) => s.name === name) || { id: name, name, description: '' }
const demoTag = (name: string) => ({ id: name, name, description: '' })

export const DEMO_SUGGESTIONS: IStageMapGroup[] = [
   { group: 'ff-nurturing', stages: [demoStage('New Lead'), demoStage('Nurture')], tags: [] },
   { group: 'ff-appointment-set', stages: [demoStage('Appointment Set')], tags: [] },
   { group: 'ff-appointment-missed', stages: [demoStage('Appointment Missed')], tags: [] },
   {
      group: 'ff-stopped',
      types: [
         { key: 'ff-under-contract', stages: [demoStage('Under Contract')], tags: [] },
         { key: 'ff-active-client', stages: [demoStage('Active Client')], tags: [] },
         { key: 'ff-closed-client', stages: [demoStage('Closed')], tags: [demoTag('sold')] },
         { key: 'ff-do-not-contact', stages: [demoStage('Trash')], tags: [demoTag('do-not-call')] },
         { key: 'ff-has-agent', stages: [], tags: [demoTag('has-agent')] },
         { key: 'ff-other-stop', stages: [], tags: [] },
      ],
   },
]
